// ─── Interaction System ───────────────────────────────────────────────────────

import {
  INTERACTION_RANGE,
  TILE_SIZE,
  type Interactable,
  type Position,
} from "./constants";
import type { Character } from "./character";

export interface InteractionResult {
  type: "building" | "section" | "surggie" | "gem";
  id: string;
  data?: Record<string, unknown>;
}

/**
 * Surggies use a tighter, deliberate range (~48px) than buildings/sections
 * so the player has to walk right up to a Surggie to talk to it.
 */
export const SURGGIE_INTERACTION_RANGE = 48;

export interface SurggieInteractable {
  id: string;
  type: "surggie";
  position: Position;
  width: number;
  height: number;
  name: string;
}

export class InteractionSystem {
  private nearestTarget: Interactable | SurggieInteractable | null = null;
  private eWasPressed: boolean = false;

  /**
   * Update interaction state — find the nearest interactable within range.
   *
   * @param player - The player character
   * @param interactables - List of interactable objects in the current room
   * @param surggies - List of Surggie NPC characters for proximity check
   * @returns Whether the interaction prompt should be shown
   */
  update(player: Character, interactables: Interactable[], surggies?: SurggieInteractable[]): boolean {
    const center = player.getCenter();
    let closest: Interactable | SurggieInteractable | null = null;
    let closestDist = Infinity;

    for (const obj of interactables) {
      const objCenter: Position = {
        x: obj.position.x + obj.width / 2,
        y: obj.position.y + obj.height / 2,
      };

      const dist = Math.hypot(center.x - objCenter.x, center.y - objCenter.y);

      if (dist < INTERACTION_RANGE && dist < closestDist) {
        closestDist = dist;
        closest = obj;
      }
    }

    // Surggies are checked against their own (tighter) range, and the nearest
    // Surggie is preferred over buildings/sections when both are in range.
    let closestSurggie: SurggieInteractable | null = null;
    let closestSurggieDist = Infinity;
    if (surggies) {
      for (const s of surggies) {
        const objCenter: Position = {
          x: s.position.x + s.width / 2,
          y: s.position.y + s.height / 2,
        };
        const dist = Math.hypot(center.x - objCenter.x, center.y - objCenter.y);
        if (dist < SURGGIE_INTERACTION_RANGE && dist < closestSurggieDist) {
          closestSurggieDist = dist;
          closestSurggie = s;
        }
      }
    }

    this.nearestTarget = closestSurggie ?? closest;
    return this.nearestTarget !== null;
  }

  /**
   * Handle the E key press — returns the interaction target if player pressed E
   * while near an interactable. Uses edge detection (triggers on key-down, not hold).
   *
   * @param keysPressed - Current set of pressed keys
   * @returns InteractionResult if interaction happened, null otherwise
   */
  handleInteraction(keysPressed: Set<string>): InteractionResult | null {
    const ePressed = keysPressed.has("e") || keysPressed.has("E");

    // Edge detection: only trigger on the frame E is first pressed
    if (ePressed && !this.eWasPressed && this.nearestTarget) {
      this.eWasPressed = true;
      const target = this.nearestTarget;
      if ("name" in target) {
        // Surggie target — include enough payload to identify it downstream.
        return {
          type: target.type,
          id: target.id,
          data: { name: target.name },
        };
      }
      return {
        type: target.type,
        id: target.id,
        data: target.data,
      };
    }

    if (!ePressed) {
      this.eWasPressed = false;
    }

    return null;
  }

  /**
   * Get the current nearest interactable target.
   */
  getNearestTarget(): Interactable | SurggieInteractable | null {
    return this.nearestTarget;
  }

  /**
   * Draw the "Press E" prompt above the nearest interactable.
   */
  drawPrompt(ctx: CanvasRenderingContext2D): void {
    if (!this.nearestTarget) return;

    const target = this.nearestTarget;
    const px = target.position.x + target.width / 2;
    const py = target.position.y - 12;

    // Background pill
    ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
    const text = "Press E";
    ctx.font = "bold 10px monospace";
    const metrics = ctx.measureText(text);
    const tw = metrics.width + 8;
    ctx.beginPath();
    ctx.roundRect(px - tw / 2, py - 8, tw, 14, 4);
    ctx.fill();

    // Text
    ctx.fillStyle = "#F4D03F";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(text, px, py);
  }
}
